import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface Mentor {
  uuid: string;
  name: string;
  email: string;
  specialization: string;
  meetings_held: number;
  date_joined: string;
}

export function MentorList() {
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadMentors() {
      try {
        const { data, error } = await supabase
          .from('mentor')
          .select(`
            user_uuid,
            specialization,
            meetings_held,
            vincer_base_user!inner (
              uuid,
              name,
              email,
              date_joined
            )
          `);

        if (error) throw error;

        // Transform the nested data structure into flat Mentor objects
        const formattedMentors: Mentor[] = data.map(mentor => ({
          uuid: mentor.user_uuid,
          name: mentor.vincer_base_user.name,
          email: mentor.vincer_base_user.email,
          specialization: mentor.specialization,
          meetings_held: mentor.meetings_held,
          date_joined: new Date(mentor.vincer_base_user.date_joined).toLocaleDateString()
        }));

        setMentors(formattedMentors);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred while loading mentors');
      } finally {
        setLoading(false);
      }
    }

    loadMentors();
  }, []);

  if (loading) {
    return <div className="text-center py-8">Loading mentors...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Mentors</h1>
        <Link
          to="/coordinator/mentors/add"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#4CAF50] hover:bg-[#45a049] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4CAF50]"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Add Mentor
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Specialization
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Meetings Held
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {mentors.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  No mentors found. Add your first mentor to get started!
                </td>
              </tr>
            ) : (
              mentors.map((mentor) => (
                <tr key={mentor.uuid} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {mentor.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {mentor.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {mentor.specialization}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {mentor.meetings_held}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {mentor.date_joined}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
} 